/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from "react";
import clsx from "clsx";
import { useTheme } from "../../../../hooks/useTheme";
import { getTagColors } from "../../../../utils/tagColors";
import { ThemeMode } from "../../../../interfaces/theme";
import { LIGHT_THEME } from "../../../../constants/theme";
import { Popover } from "@headlessui/react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowRightFromLine,
  ArrowUpFromLine,
  Banknote,
  Cable,
  Check,
  Clock,
  Copy,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  Repeat2,
  ShoppingCart,
  Tag,
  Trash2,
  WrapText,
  X,
} from "lucide-react";
import { ErrorModalContext } from "../../../AlertsModal/AlertsModal";
import ToolTip from "../../../ToolTip/ToolTip";
import { AtmSourceTag } from "../../../Table/utils/AtmSourceTag";
import { CardRechargeTag } from "../../../CardRechargeTag/CardRechargeTag";
import { formatCurrency } from "../../../../utils/moneyFormat";
import { formatDate } from "../../../../utils/dates";
import { isBlankString } from "../../../../utils/strings";
import { getSignedDownloadUrl } from "../../../../services/files";
import {
  getTicketCardActionBody,
  getTicketCardTypeBody,
} from "../../../../translates/ticketsCards";

import { getTranslatedSaleStatus } from "../../../../translates/sales/sales";
import { getTranslatedApiCredentialStatus } from "../../../../translates/apiCredentials/status";
import { ApiCredentialStatus } from "../../../../interfaces/apiCredentials";
import { FieldType, Space, TdProps } from "../interfaces";
import { TableBillStatus } from "../../../../interfaces/TableBills";
import ListSelect from "../../../ListSelect/ListSelect";
import { ChevronDown } from "lucide-react";
import { GroupConfig } from "../../../../interfaces/concessions";
import { Switch } from "../../Form/component/Switch";
import { profiles } from "../../../../profiles";
import { translateUserProfiles } from "../../../../translates/users/profiles";
import {
  MovementType,
  MovementTypeNames,
} from "../../../../interfaces/inventoryTransfer";
import { InventoryMovement } from "../../../../interfaces/inventoryMovements";
import {
  TransactionStatus,
  TransactionType,
} from "../../../../screens/SuperAdmin/Transactions/BankCards/components";
import { CashlessStatus } from "../../../../screens/Cashless/TicketsCards/utils/cashlessEnum";
import { ExitsBreakdownPopover } from "../../../../screens/Inventory/InventorySummary/components/ExitsBreakdownPopover";

const renderTooltipContent = (value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1">
        {value.map((item, i) => (
          <li
            key={i}
            className="capitalize"
          >
            {String(item)}
          </li>
        ))}
      </ul>
    );
  }
  return <span>{String(value)}</span>;
};

export const Td: React.FC<TdProps> = ({
  align = "left",
  firstFieldType = "text",
  firstFieldValue,
  hasParent = false,
  isCompact = false,
  isFirstFieldSubtitle = false,
  isSecondFieldSubtitle = false,
  minWidth = 100,
  openCommentModal,
  redirectToConcessions,
  row,
  secondFieldType = "text",
  secondFieldValue,
  space,
  textColor,
  venueUUID,
  weight = 1,
  onFunction,
  tooltipKey,
}) => {
  const { showAlert } = useContext(ErrorModalContext);
  const { theme } = useTheme();

  const firstContent = formatContent(
    firstFieldValue,
    firstFieldType,
    isFirstFieldSubtitle,
    hasParent,
    space,
    showAlert,
    redirectToConcessions,
    venueUUID,
    openCommentModal,
    row,
    onFunction,
    align,
    theme,
  );

  const tooltipValue = tooltipKey ? row?.[tooltipKey] : undefined;
  const wrappedFirstContent =
    tooltipValue !== undefined && tooltipValue !== null ? (
      <ToolTip content={renderTooltipContent(tooltipValue)}>
        {firstContent}
      </ToolTip>
    ) : (
      firstContent
    );

  return (
    <td
      className={`flex-1 ${isCompact ? "px-4 py-1.5 text-xs" : "p-4 text-sm"} ${
        space === "body" ? "font-normal" : "font-semibold"
      } ${textColor || "text-gray-900"}`}
      style={{ minWidth: `${minWidth}px`, textAlign: align, flex: weight }}
    >
      <div
        className={`${hasParent ? "flex items-center gap-2 pl-4" : ""} ${
          firstFieldType === "quantity-with-pending" && align === "right"
            ? "flex justify-end"
            : ""
        }`}
      >
        {hasParent && <span className="text-gray-400">—</span>}
        <div
          className={`flex ${
            secondFieldType === "badge"
              ? "flex-row items-center gap-2"
              : "flex-col gap-1"
          } ${
            firstFieldType === "quantity-with-pending" && align === "right"
              ? "w-full"
              : ""
          }`}
        >
          {wrappedFirstContent}
          {(secondFieldValue || secondFieldValue === 0) &&
            formatContent(
              secondFieldValue,
              secondFieldType,
              isSecondFieldSubtitle,
              hasParent,
              space,
              showAlert,
              undefined,
              undefined,
              openCommentModal,
              row,
              undefined,
              align,
              theme,
            )}
        </div>
      </div>
    </td>
  );
};

const formatContent = (
  value: string | number | any,
  type: FieldType,
  isSubtitle: boolean,
  hasParent: boolean,
  space: Space,
  showAlert: (
    message: string,
    type: "success" | "error",
    title?: string,
  ) => void,
  redirectToConcessions?: (venueUUID: string) => void,
  venueUUID?: string,
  openCommentModal?: (row: any) => void,
  row?: any,
  onFunction?: (value: any) => void,
  align: string = "left",
  theme: ThemeMode = LIGHT_THEME,
): JSX.Element => {
  if (space === "footer" && (value ?? "") === "") return <span></span>;

  if (type === "money")
    return (
      <span
        className={`whitespace-nowrap ${
          isSubtitle ? "text-xs text-gray-500" : ""
        }`}
      >
        {formatCurrency(typeof value === "string" ? Number(value) : value)}
      </span>
    );

  if (type === "cashless-status")
    return (
      <div className="flex items-center justify-center">
        <div
          className={`rounded-full ${isSubtitle ? "h-2 w-2" : "h-4 w-4"} ${
            value === CashlessStatus.ACTIVE
              ? "bg-green-500"
              : value === CashlessStatus.INACTIVE ||
                  value === CashlessStatus.CANCELLED
                ? "bg-gray-300"
                : "bg-yellow-500"
          }`}
        ></div>
      </div>
    );

  if (type === "kitchen-station-status") {
    const statusColors: Record<string, string> = {
      Consultando: "bg-orange-100 text-orange-800 border-orange-300",
      Conectado: "bg-green-100 text-green-800 border-green-300",
      Desconectado: "bg-gray-100 text-gray-800 border-gray-300",
      Error: "bg-red-100 text-red-800 border-red-300",
      Imprimiendo: "bg-blue-100 text-blue-800 border-blue-300",
    };

    const colorClass: string =
      statusColors[value as string] || statusColors["Desconectado"];

    return (
      <div className="flex items-center justify-center">
        <span
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${colorClass}`}
        >
          {value}
        </span>
      </div>
    );
  }

  if (type === "atm-status") {
    const statusConfig: Record<string, { label: string; className: string }> = {
      green: {
        label: "En línea",
        className: "bg-green-100 text-green-800 border-green-300",
      },
      yellow: {
        label: "Advertencia",
        className: "bg-amber-100 text-amber-800 border-amber-300",
      },
      red: {
        label: "Sin conexión",
        className: "bg-red-100 text-red-800 border-red-300",
      },
    };

    const config = statusConfig[value as string] || statusConfig.red;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${config.className}`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {config.label}
      </span>
    );
  }

  // YOSHI-10464: lifecycle of a partner API credential. The value is derived from revoked_at and
  // expires_at before the row reaches the table, so this branch only paints it.
  if (type === "api-credential-status") {
    const credentialStatusClassName: Record<string, string> = {
      active: "bg-green-100 text-green-800 border-green-300",
      expired: "bg-amber-100 text-amber-800 border-amber-300",
      revoked: "bg-red-100 text-red-800 border-red-300",
    };

    const credentialStatus: ApiCredentialStatus = value as ApiCredentialStatus;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
          credentialStatusClassName[credentialStatus] ||
          credentialStatusClassName.revoked
        }`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {getTranslatedApiCredentialStatus(credentialStatus)}
      </span>
    );
  }

  if (type === "atm-operational-status") {
    const status = row?.operational_status;
    const colorClass =
      status === "operating"
        ? "bg-green-100 text-green-800 border-green-300"
        : status === "available"
          ? "bg-blue-100 text-blue-800 border-blue-300"
          : "bg-gray-100 text-gray-700 border-gray-300";
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${colorClass}`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {value}
      </span>
    );
  }

  // YOSHI-10096: cabinet colour derived on the backend from the serial prefix (ATMYB / ATMY).
  if (type === "atm-type") {
    if (!value) {
      return <span className="text-sm text-gray-400">—</span>;
    }
    const isBlack = row?.atm_type === "black";
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
          isBlack
            ? "border-gray-700 bg-gray-800 text-white"
            : "border-gray-300 bg-white text-gray-800"
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full border ${
            isBlack ? "border-white bg-gray-900" : "border-gray-400 bg-white"
          }`}
        />
        {value}
      </span>
    );
  }

  if (type === "atm-assignment-status") {
    const isActive = value === "Activa";
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
          isActive
            ? "bg-green-100 text-green-800 border-green-300"
            : "bg-gray-100 text-gray-700 border-gray-300"
        }`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {value}
      </span>
    );
  }

  // ROI reads as a multiple, and whether it clears 1x is the whole point — below it the incentive gave
  // away more than it moved, which is a warning and not an error. A null value means there is no ROI to
  // show, which is not the same as a bad return, so it stays neutral.
  if (type === "roi") {
    if (value === null || value === undefined || value === "") {
      return <span className="font-semibold text-gray-400">sin ROI</span>;
    }

    const roi: number = typeof value === "string" ? Number(value) : value;
    return (
      <span
        className={`font-semibold ${
          roi >= 1 ? "text-green-600" : "text-amber-600"
        }`}
      >
        {`${roi.toFixed(2)}x`}
      </span>
    );
  }

  if (type === "atm-source") {
    return (
      <div className="flex justify-center">
        <AtmSourceTag source={(value as string) ?? null} />
      </div>
    );
  }

  if (type === "atm-log-detail") {
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => onFunction?.(row)}
          className="rounded-md p-1 transition-colors hover:bg-gray-100"
        >
          <Banknote className="h-5 w-5 text-success-hover" />
        </button>
      </div>
    );
  }

  if (type === "atm-cut-status") {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {row?.has_discrepancy ? (
          <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
            No cuadra
          </span>
        ) : (
          <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
            Cuadra
          </span>
        )}
        {row?.has_link_inconsistency && (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
            Revisar
          </span>
        )}
      </div>
    );
  }

  if (type === "concessions-badges") {
    const concessions: string[] = Array.isArray(value)
      ? value.filter(
          (item): item is string =>
            typeof item === "string" && item.trim().length > 0,
        )
      : [];

    if (concessions.length === 0) {
      return <span className="text-xs text-gray-500">Sin concesiones</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {concessions.map((concession) => (
          <span
            key={concession}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700"
          >
            <Tag className="h-3 w-3 text-gray-500" />
            {concession}
          </span>
        ))}
      </div>
    );
  }

  if (type === "boolean")
    return (
      <div className="flex items-center justify-center">
        <div
          className={`rounded-full ${isSubtitle ? "h-2 w-2" : "h-4 w-4"} ${
            value ? "bg-green-500" : "bg-gray-300"
          }`}
        ></div>
      </div>
    );

  if (type === "percentage")
    return (
      <span
        className={`break-words ${isSubtitle ? "text-xs text-gray-500" : ""}`}
      >
        {`${Number(value || 0).toFixed(2)}%`}
      </span>
    );

  if (type === "check") {
    if (value === null || value === undefined) {
      return <span></span>;
    }

    if (value === 0) {
      return (
        <div className="flex items-center justify-center">
          <X className="h-5 w-5 text-gray-500" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <Check className="h-5 w-5 text-green-500" />
      </div>
    );
  }

  if (type === "date") {
    // A nullable date column (e.g. a season with no closing date) used to throw
    // here and take the whole screen down through the error boundary. Renders
    // blank instead, following the `value?.toString()` guard the other branches
    // of this formatter already use.
    const rawDate: string = value?.toString() ?? "";

    return (
      <span
        className={`break-words ${isSubtitle ? "text-xs text-gray-500" : ""}`}
      >
        {rawDate === "" ? "" : formatDate(rawDate)}
      </span>
    );
  }

  if (type === "copy-paste") {
    return (
      <div className="flex items-center gap-2 font-semibold">
        {/* YOSHI-10464: the value is a flex item, so without min-w-0 it refuses to shrink below the
            width of its own text and a long identifier spills over the next column instead of
            wrapping. The column still decides the width; this only keeps the overflow contained. */}
        <span className="min-w-0 break-words">{value}</span>
        {value && (
          <Copy
            className="h-4 w-4 shrink-0 cursor-pointer text-gray-500 transition-colors hover:text-gray-800"
            onClick={() => {
              navigator.clipboard.writeText(value.toString());
              showAlert(
                "Valor copiado correctamente",
                "success",
                "Copiado al portapapeles",
              );
            }}
          />
        )}
      </div>
    );
  }

  if (type === "session-counter") {
    // Same nullable-value exposure as the "date" branch above: a session row
    // with no counter would throw and break the whole table.
    const rawCounter: string = value?.toString() ?? "";

    if (rawCounter === "") {
      return <span className="break-words text-xs text-gray-500"></span>;
    }

    if (rawCounter.includes("Sesión Terminada"))
      return (
        <span className="break-words text-xs text-gray-500">
          Sesión Terminada
        </span>
      );

    if (rawCounter.includes("Inicia"))
      return <span className="break-words text-xs text-blue-500">{value}</span>;

    const counter: number = parseInt(rawCounter);

    return (
      <span
        className={`break-words text-xs ${
          counter === 0
            ? "text-gray-500"
            : counter < 10
              ? "text-orange-500"
              : counter < 30
                ? "text-yellow-500"
                : "text-green-600"
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "ticket-card-action") {
    const config = getTicketCardActionBody(value as string);

    return (
      <div className="flex items-center justify-center">
        <span
          className={`inline-block w-fit min-w-[80px] rounded-full px-3 py-1 text-xs font-normal
           ${config.tagColor} ${config.tagTextColor}`}
        >
          {config.tagText}
        </span>
      </div>
    );
  }

  if (type === "ticket-card-type") {
    const config = getTicketCardTypeBody(value as number);

    return (
      <div className="flex items-center justify-center">
        <span
          className={`inline-block w-fit min-w-[80px] rounded-full px-3 py-1 text-xs font-normal
           ${config.tagColor} ${config.tagTextColor}`}
        >
          {config.tagText}
        </span>
      </div>
    );
  }

  if (type === "bill-status") {
    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${
          value === 0
            ? "bg-red-100 text-red-800"
            : value === 1
              ? "bg-blue-100 text-blue-800"
              : value === 2
                ? "bg-gray-100 text-gray-800"
                : value === 3
                  ? "bg-yellow-100 text-yellow-800"
                  : value === 5
                    ? "bg-amber-100 text-amber-800"
                    : "bg-green-100 text-green-800"
        }`}
      >
        {TableBillStatus[value]}
      </span>
    );
  }

  if (type === "sale-status") {
    const statusText = getTranslatedSaleStatus(Number(value));
    let bgColor = "bg-gray-100 text-gray-800";

    switch (value) {
      case 0:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 1:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 2:
        bgColor = "bg-green-100 text-green-800";
        break;
      case 3:
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case 4:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 5:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 6:
        bgColor = "bg-gray-100 text-gray-800";
        break;
      case 7:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 8:
        bgColor = "bg-orange-100 text-orange-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
        break;
    }

    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
      >
        {statusText}
      </span>
    );
  }
  if (type === "sale-status-with-pending") {
    const hasPendingCancellation = row?.has_pending_cancellation || false;
    const statusText = getTranslatedSaleStatus(Number(value));
    let bgColor = "bg-gray-100 text-gray-800";

    switch (value) {
      case 0:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 1:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 2:
        bgColor = "bg-green-100 text-green-800";
        break;
      case 3:
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case 4:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 5:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 6:
        bgColor = "bg-gray-100 text-gray-800";
        break;
      case 7:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 8:
        bgColor = "bg-orange-100 text-orange-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
        break;
    }

    return (
      <div className="group/sale-status relative flex w-full items-center justify-center gap-2">
        {hasPendingCancellation && (
          <>
            <Clock className="h-4 w-4 shrink-0 text-amber-600" />
            <div className="absolute left-1/2 top-full z-50 mt-2 hidden w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover/sale-status:block">
              <p className="font-medium">Cancelación pendiente de aprobación</p>
              <p className="mt-1 text-gray-300">
                Esta venta tiene una solicitud de cancelación que aún no ha sido
                aprobada.
              </p>
            </div>
          </>
        )}
        <span
          className={`w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
        >
          {statusText}
        </span>
      </div>
    );
  }

  if (type === "pending-request-status") {
    let bgColor = "bg-yellow-100 text-yellow-800";
    let statusText = "Pendiente";

    if (value === 1) {
      bgColor = "bg-green-100 text-green-800";
      statusText = "Aprobada";
    } else if (value === 2) {
      bgColor = "bg-red-100 text-red-800";
      statusText = "Rechazada";
    }

    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
      >
        {statusText}
      </span>
    );
  }

  if (type === "cancellation-type") {
    const bgColor =
      value === "Total"
        ? "bg-red-100 text-red-800"
        : "bg-yellow-100 text-yellow-800";
    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
      >
        {value}
      </span>
    );
  }
  if (type === "cancellation-status") {
    const bgColor =
      value === "Reembolso pendiente"
        ? "bg-yellow-100 text-yellow-800"
        : "bg-green-100 text-green-800";
    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
      >
        {value}
      </span>
    );
  }
  if (type === "balance-cut-status") {
    // Raw status from the backend → label + color. settled ("Liquidado") is reserved.
    const balanceCutStatusMap: Record<
      string,
      { label: string; className: string }
    > = {
      in_refund_period: {
        label: "Abierto",
        className: "bg-yellow-100 text-yellow-800",
      },
      closed: { label: "Cerrado", className: "bg-blue-100 text-blue-800" },
      settled: { label: "Liquidado", className: "bg-green-100 text-green-800" },
    };
    const balanceCutStatus = balanceCutStatusMap[String(value)] ?? {
      label: String(value ?? ""),
      className: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${balanceCutStatus.className}`}
      >
        {balanceCutStatus.label}
      </span>
    );
  }
  if (type === "concessions-count-color" && typeof value === "string") {
    return (
      <span className="flex flex-row items-center  justify-end gap-3">
        <span
          className="hover:underline"
          onClick={() => redirectToConcessions?.(venueUUID ?? "")}
        >
          {value.split("_")[0]}
        </span>
        <span
          className={`h-2 w-2 rounded-full ${
            value.split("_")[1] === "0" ? "bg-green-500" : "bg-orange-500"
          }`}
        />
      </span>
    );
  }

  if (type === "fee-payer") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          !value || typeof value !== "string"
            ? ""
            : value === "Concesión"
              ? "bg-purple-100 text-purple-800"
              : value === "Ambos"
                ? "bg-orange-100 text-orange-800"
                : value === "Cliente"
                  ? "bg-blue-100 text-blue-800"
                  : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "user-profile") {
    return (
      <span
        className={`mx-auto inline-block break-words rounded-full px-3 py-1 text-center text-xs font-medium ${
          value === profiles.superAdmin || value === profiles.consultation
            ? "bg-red-100 text-red-800"
            : value === profiles.venuesAdmin
              ? "bg-blue-100 text-blue-800"
              : value === profiles.concessionAdmin || value === profiles.pos
                ? "bg-green-100 text-green-800"
                : value === profiles.recharges || value === profiles.seller
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
        }`}
      >
        {translateUserProfiles[value]}
      </span>
    );
  }

  if (type === "liquidation-method") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "PPD"
            ? "bg-purple-100 text-purple-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "payments-accepted" && typeof value === "string" && value) {
    const paymentsAccepted: string[] = value.split(", ");

    return (
      <span className="flex flex-wrap items-center gap-2">
        {paymentsAccepted.map((payment) => (
          <span
            key={payment}
            className={`inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
              payment === "Tarjeta"
                ? "bg-green-100 text-green-800"
                : payment === "QR"
                  ? "bg-purple-100 text-purple-800"
                  : payment === "NFC"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-orange-100 text-orange-800"
            }`}
          >
            {payment}
          </span>
        ))}
      </span>
    );
  }

  if (type === "payment-method" && (typeof value !== "string" || !value)) {
    return <span></span>;
  }

  if (type === "payment-method" && typeof value === "string" && value) {
    const paymentMethodColors: Record<string, string> = {
      Efectivo: "bg-green-100 text-green-800",
      Tarjeta: "bg-blue-100 text-blue-800",
      "QR Wallet": "bg-purple-100 text-purple-800",
      "QR Ticket": "bg-violet-100 text-violet-800",
      NFC: "bg-orange-100 text-orange-800",
      Palma: "bg-amber-100 text-amber-800",
      Token: "bg-teal-100 text-teal-800",
      SPEI: "bg-cyan-100 text-cyan-800",
    };
    const methods: string[] = value.split(", ");

    return (
      <span className="flex flex-wrap items-center gap-2">
        {methods.map((method) => (
          <span
            key={method}
            className={`inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
              paymentMethodColors[method] || "bg-gray-100 text-gray-800"
            }`}
          >
            {method}
          </span>
        ))}
      </span>
    );
  }

  if (type === "order-tag") {
    if (isBlankString(value)) {
      return <span></span>;
    }
    return (
      <span className="inline-block w-fit break-words rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        {value}
      </span>
    );
  }

  if (type === "card-recharge-tag") {
    if (value !== true) {
      return <span></span>;
    }

    return (
      <div
        className={clsx(
          "flex",
          align === "center" && "justify-center",
          align === "right" && "justify-end",
        )}
      >
        <CardRechargeTag />
      </div>
    );
  }

  if (type === "discount-type") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "Fijo"
            ? "bg-orange-100 text-orange-800"
            : "bg-blue-100 text-blue-800"
        }`}
      >
        {value}
      </span>
    );
  }
  if (type === "discount-scope") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "Global"
            ? "bg-purple-100 text-purple-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {value}
      </span>
    );
  }
  if (type === "groups" && value) {
    const groups: GroupConfig[] = JSON.parse(value as string);
    const justify: string =
      align === "center"
        ? "justify-center"
        : align === "right"
          ? "justify-end"
          : "justify-start";

    return (
      <div className={`flex flex-wrap items-center gap-2 ${justify}`}>
        {groups.map((group: GroupConfig) => (
          <span
            key={group.name}
            className="rounded-full px-2 py-1 text-center text-xs font-medium"
            style={getTagColors(group.bg_color, group.text_color, theme)}
          >
            {group.name}
          </span>
        ))}
      </div>
    );
  }

  if (type === "warehouse-scope") {
    const warehouses: string[] = value;

    return (
      <span className="flex flex-wrap items-center gap-2">
        {warehouses.length === 0 ? (
          <span className="inline-block rounded-full border border-green-200 bg-green-50 px-2 py-1 text-center text-xs font-medium text-green-700">
            General
          </span>
        ) : (
          <>
            {warehouses.slice(0, 3).map((warehouse, index) => (
              <span
                key={index}
                className="inline-block rounded-full border border-gray-200 bg-white px-2 py-1 text-center text-xs font-medium text-gray-800"
              >
                {warehouse}
              </span>
            ))}
            {warehouses.length > 3 && (
              <span className="inline-block rounded-full border border-gray-200 bg-white px-2 py-1 text-center text-xs font-medium text-gray-800">
                {`${warehouses.length - 3} más`}
              </span>
            )}
          </>
        )}
      </span>
    );
  }

  if (type === "quantity-with-pending") {
    const hasPendingTransactions = row?.has_pending_transactions || false;

    return (
      <div className="group/quantity relative flex w-full items-center justify-end gap-2">
        <span
          className={`break-words ${
            hasPendingTransactions ? "text-amber-600" : ""
          }`}
        >
          {value}
        </span>
        {hasPendingTransactions && (
          <>
            <Clock className="h-4 w-4 shrink-0 text-amber-600" />
            <div className="absolute right-0 top-full z-10 mt-2 hidden w-64 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover/quantity:block">
              <p className="font-medium">Transacciones pendientes</p>
              <p className="mt-1 text-gray-300">
                La disponibilidad muestra cantidades que ya fueron solicitadas
                pero aún no han sido aprobadas por el almacén destino.
              </p>
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "movement-type") {
    const movementType = value as MovementType;

    return (
      <span
        className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
          movementType === "entry" || movementType === "event_restock"
            ? "border-green-200 bg-green-50 text-green-700"
            : movementType === "exit"
              ? "border-pink-200 bg-pink-50 text-pink-700"
              : movementType === "waste"
                ? "border-orange-200 bg-orange-50 text-orange-700"
                : movementType === "transfer"
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : movementType === "sale"
                    ? "border-purple-200 bg-purple-50 text-purple-700"
                    : movementType === "sale_cancel"
                      ? "border-red-200 bg-red-50 text-red-700"
                      : movementType === "product_change"
                        ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                        : "border-yellow-200 bg-yellow-50 text-yellow-700"
        }`}
      >
        {movementType === "entry" || movementType === "event_restock" ? (
          <ArrowDownToLine className="h-3.5 w-3.5" />
        ) : movementType === "exit" ? (
          <ArrowUpFromLine className="h-3.5 w-3.5" />
        ) : movementType === "waste" ? (
          <Trash2 className="h-3.5 w-3.5" />
        ) : movementType === "transfer" ? (
          <ArrowRightFromLine className="h-3.5 w-3.5" />
        ) : movementType === "sale" ? (
          <ShoppingCart className="h-3.5 w-3.5" />
        ) : movementType === "sale_cancel" ? (
          <X className="h-3.5 w-3.5" />
        ) : movementType === "product_change" ? (
          <Repeat2 className="h-3.5 w-3.5" />
        ) : (
          <WrapText className="h-3.5 w-3.5" />
        )}
        {MovementTypeNames[movementType]}
      </span>
    );
  }

  const getValue = (
    value: string | number | undefined,
    fieldType?: string,
  ): string => {
    if (typeof value === "number") return value.toLocaleString();
    if (typeof value === "undefined") return "-";
    if (value === "") return "-";
    if (value === null) return "-";

    // Don't transform text-no-capitalize fields
    if (fieldType === "text-no-capitalize") return value;

    if (/^[A-Z0-9]+$/.test(value) && !isSubtitle) return value.toUpperCase();
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  if (type === "movement-comment") {
    const movement = row as InventoryMovement;
    // YOSHI-10261: el cambio de producto tambien trae el codigo de la venta en el comentario
    const isSaleLinkedMovement: boolean =
      movement.movement_type === "sale" ||
      movement.movement_type === "sale_cancel" ||
      movement.movement_type === "product_change";
    const hasCommentOrFile = !!(movement.comment || movement.urls?.length);

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-words capitalize">{getValue(value)}</span>
        {!isSaleLinkedMovement && hasCommentOrFile && (
          <button
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 transition-colors duration-300 hover:bg-gray-200"
            onClick={() => openCommentModal?.(movement)}
          >
            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          </button>
        )}
      </div>
    );
  }

  if (type === "redirect-to-sale") {
    const movement = row as InventoryMovement;
    const [saleCode, saleUUID] = movement.comment
      ? movement.comment.split(" | ")
      : [];
    // YOSHI-10261: el cambio de producto tambien liga a su venta / cuenta
    const isSaleLinkedMovement: boolean =
      movement.movement_type === "sale" ||
      movement.movement_type === "sale_cancel" ||
      movement.movement_type === "product_change";

    const redirectToSale: boolean = saleUUID ? saleUUID.includes("sal") : false;

    return (
      <div className="flex flex-col gap-1">
        <span className="break-words capitalize">{getValue(value)}</span>
        {isSaleLinkedMovement && saleCode && saleUUID && (
          <Link
            to={`/panel/${redirectToSale ? "ordenes" : "cuentas"}/${saleUUID}`}
            className="w-fit max-w-[218px] truncate text-xs text-gray-500 underline transition-colors duration-300 hover:text-gray-700"
          >
            {`${redirectToSale ? "Venta" : "Cuenta"}: ${saleCode}`}
          </Link>
        )}
      </div>
    );
  }

  if (type === "card-number") {
    return (
      <span
        className={`inline-block break-words align-baseline font-mono leading-normal    ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "text-no-capitalize") {
    return (
      <span
        className={`inline-block whitespace-pre-line break-words align-baseline  leading-normal    ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "function") {
    return (
      <span
        onClick={() => onFunction?.(row)}
        className="cursor-pointer hover:underline"
      >
        {value}
      </span>
    );
  }

  if (type === "transaction-status") {
    return <TransactionStatus status={value as number} />;
  }

  if (type === "transaction-type") {
    return <TransactionType type={value as string} />;
  }

  if (type === "file-icons") {
    const rowData = row as any;
    const receiptUrl = rowData.receipt_url;
    const imageUrl = rowData.image_url;
    let isDownloading: string | null = null;

    const handleFileClick = async (fileUrl: string) => {
      try {
        isDownloading = fileUrl;
        const signedUrl = await getSignedDownloadUrl(fileUrl);
        window.open(signedUrl, "_blank");
      } catch (error) {
        console.error("Error getting signed URL:", error);
        // Fallback to direct URL if signed URL fails
        window.open(fileUrl, "_blank");
      } finally {
        isDownloading = null;
      }
    };

    return (
      <div className="flex items-center justify-center gap-3">
        {receiptUrl && (
          <button
            className="flex items-center justify-center transition-colors duration-300 hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick(receiptUrl);
            }}
            title="Ver comprobante"
            disabled={isDownloading !== null}
          >
            {isDownloading === receiptUrl ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
            ) : (
              <FileText className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {imageUrl && (
          <button
            className="flex items-center justify-center transition-colors duration-300 hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick(imageUrl);
            }}
            title="Ver evidencia"
            disabled={isDownloading !== null}
          >
            {isDownloading === imageUrl ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
            ) : (
              <ImageIcon className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {!receiptUrl && !imageUrl && (
          <span className="text-xs text-gray-400">Sin archivos</span>
        )}
      </div>
    );
  }

  if (type === "text-bold") {
    return (
      <span
        className={`break-words font-medium ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "beneficiary-status") {
    // Check if we should hide the dot completely
    if (row?.hideBeneficiaryStatus) {
      return <div className="flex items-center justify-center"></div>;
    }

    const hasBankInfo =
      row?.bank_information &&
      row.bank_information.beneficiary &&
      row.bank_information.beneficiary.trim() !== "" &&
      row.bank_information.bank_name &&
      row.bank_information.bank_name.trim() !== "" &&
      row.bank_information.main_account &&
      row.bank_information.main_account.trim() !== "";

    return (
      <div className="flex items-center justify-center">
        <div
          className={`mt-1.5 rounded-full ${
            isSubtitle ? "h-2 w-2" : "h-4 w-4"
          } ${hasBankInfo ? "bg-green-500" : "bg-red-500"}`}
        ></div>
      </div>
    );
  }

  if (type === "signal") {
    // Parse value: format is "signalStrength_text_accessPoint_ethernet_ethernetStatus"
    const parts = value?.toString().split("_text_") || [];
    const signalStrength = parseInt(parts[0] || "0");
    const remainingParts = parts[1]?.split("_ethernet_") || [];
    const accessPoint = remainingParts[0] || "";
    const ethernetStatus = remainingParts[1]
      ? parseInt(remainingParts[1])
      : undefined;

    // If ethernet is connected, show ethernet icon instead of signal bars
    if (ethernetStatus === 1) {
      return (
        <div className="flex flex-row items-center gap-2">
          <Cable className="h-4 w-4 text-green-600" />
          <span className="text-xs text-gray-900">Ethernet</span>
        </div>
      );
    }

    // Original signal bar rendering with original table styling
    const activeColor =
      signalStrength >= 50
        ? "green-500"
        : signalStrength >= 25
          ? "amber-500"
          : "red-500";

    if (signalStrength >= 75) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[14px] w-1 rounded-sm bg-${activeColor}`}></div>
          {accessPoint && (
            <span className="ml-1 text-xs text-gray-500">{accessPoint}</span>
          )}
        </div>
      );
    }

    if (signalStrength >= 50 && signalStrength < 75) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
          {accessPoint && (
            <span className="ml-1 text-xs text-gray-500">{accessPoint}</span>
          )}
        </div>
      );
    }

    if (signalStrength >= 25 && signalStrength < 50) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
          {accessPoint && (
            <span className="ml-1 text-xs text-gray-500">{accessPoint}</span>
          )}
        </div>
      );
    }

    return (
      <div className="flex h-5 flex-row items-end gap-0.5">
        <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
        <div className="h-[8px] w-1 rounded-sm bg-gray-300"></div>
        <div className="h-[11px] w-1 rounded-sm bg-gray-300"></div>
        <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
        {accessPoint && (
          <span className="ml-1 text-xs text-gray-500">{accessPoint}</span>
        )}
      </div>
    );
  }

  if (type === "battery") {
    const [batteryLevel, isCharging] = value
      ?.toString()
      .split("_ischarging_") || ["0", "0"];

    if (batteryLevel >= 65) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }
    if (batteryLevel >= 30 && batteryLevel < 65) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }

    if (batteryLevel >= 10 && batteryLevel < 30) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }

    return (
      <div className="flex h-5 flex-row items-center gap-0.5">
        <div
          className={`h-3 w-2 animate-[pulse_0.5s_ease-in-out_infinite] rounded-md ${
            isCharging === "1" ? "bg-blue-500" : "bg-red-500"
          }`}
        ></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        {isCharging === "1" && <span className="text-xs">⚡</span>}
      </div>
    );
  }

  if (type === "row-with-subtitle") {
    const [title, subtitle] = value?.toString().split("_text_") || ["", ""];

    return (
      <div className="flex flex-row items-baseline gap-1">
        <span className="text-sm ">{title}</span>
        <span className="text-xs text-gray-500">v. {subtitle || "N/A"}</span>
      </div>
    );
  }

  if (type === "latency-tag") {
    if (value === 0 || value === null) {
      const offlineTime = row?.offline_time;
      return (
        <span className="inline-block w-fit self-end rounded-full bg-gray-500 px-4 py-1  text-xs font-medium text-white">
          {offlineTime ? `Offline - ${offlineTime}` : "Offline"}
        </span>
      );
    }
    if (value < 150) {
      return (
        <span className="inline-block w-fit self-end rounded-full bg-green-100 px-4 py-1  text-xs font-medium text-green-700">
          Buena
        </span>
      );
    }

    if (value < 300) {
      return (
        <div className="inline-block w-fit self-end rounded-full bg-amber-100 px-4 py-1  text-xs font-medium text-amber-700">
          regular
        </div>
      );
    }

    return (
      <div className="inline-block w-fit self-end rounded-full bg-red-100 px-4 py-1  text-xs font-medium text-red-700">
        mala
      </div>
    );
  }

  if (type === "primary-speed") {
    const [speed, category] = value?.toString().split("_text_") || ["", ""];

    if (category === "5") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-500">
            WIFI {category}.0
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }
    if (category === "2.4" || category === "2.4.0") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-500">
            WIFI {category}
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }

    if (category === "0") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
            Desconocido
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }

    return (
      <div className="flex flex-row items-baseline gap-1">
        <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-500">
          {category.toUpperCase()}
        </span>
        <span className="text-xs">{speed} Mbps</span>
      </div>
    );
  }

  if (type === "text-font-medium") {
    return (
      <span
        className={`break-words font-medium ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "comment-subtitle") {
    const hasValue = value && value.toString().trim() !== "";
    return (
      <span
        className={`break-words ${hasValue ? "text-xs text-gray-500" : ""}`}
      >
        {value || ""}
      </span>
    );
  }

  if (type === "row-actions") {
    return (
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {value}
      </div>
    );
  }

  if (type === "switch") {
    const rowData = row as any;
    const isLoading = rowData.isLoadingStatus || false;
    return (
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Switch
          checked={Boolean(value)}
          onChange={() => {
            if (onFunction) {
              onFunction(row);
            }
          }}
          isLoading={isLoading}
        />
      </div>
    );
  }

  if (type === "badge") {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return <span></span>;
    }
    return (
      <span className="inline-flex w-fit items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
        {value}
      </span>
    );
  }

  if (type === "concessions-list") {
    const names: string[] = Array.isArray(value) ? value : [];
    if (names.length === 0) {
      return <span>-</span>;
    }
    const first: string = names[0];
    const rest: number = names.length - 1;
    if (rest === 0) {
      return <span className="break-words capitalize">{first}</span>;
    }
    return (
      <>
        <span className="break-words capitalize">{first}</span>
        <span>{` y ${rest} más`}</span>
      </>
    );
  }

  if (type === "presentation-selector") {
    const presentations: {
      uuid: string;
      name: string;
      conversion_factor: number;
    }[] = row?.presentations ?? [];
    const currentIndex: number = row?._presentation_index ?? 0;

    if (presentations.length <= 1) {
      return (
        <span className="inline-block break-words align-baseline leading-normal">
          {value}
        </span>
      );
    }

    const dropdownItems = presentations.map((pres, i) => ({
      id: String(i),
      name: pres.name,
    }));

    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 focus:outline-none"
            >
              <span>{presentations[currentIndex]?.name ?? value}</span>
              <ChevronDown className="h-3 w-3 shrink-0 text-gray-500" />
            </Popover.Button>
            <Popover.Panel
              className="absolute left-0 top-full z-[1000] mt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <ListSelect
                items={dropdownItems}
                open={open}
                onChange={(item) => {
                  onFunction?.({
                    uuid: row._presentation_uuid ?? row.uuid,
                    index: parseInt(item.id, 10),
                  });
                  close();
                }}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>
    );
  }

  if (type === "exits-with-waste") {
    const rowData = row as any;
    const exits = Number(value) || 0;
    const waste =
      rowData && typeof rowData.waste !== "undefined"
        ? Number(rowData.waste)
        : 0;
    const total = exits + waste;

    return (
      <div className="flex items-center justify-end gap-1">
        <span>{total}</span>
        {(exits > 0 || waste > 0) && (
          <ExitsBreakdownPopover
            exits={exits}
            waste={waste}
          />
        )}
      </div>
    );
  }

  return (
    <span
      className={`break-words ${
        typeof value === "string" && value.includes(" hrs") ? "" : "capitalize"
      } ${
        isSubtitle ? "text-xs text-gray-500" : hasParent ? "text-gray-500" : ""
      }`}
    >
      {getValue(value, type)}
    </span>
  );
};
